import type { DateContext } from '@/utils/status';
import { ArrivalStatus, ReservationStatus } from '@restaurant/types';

export type ReservationAction =
  | 'confirm'
  | 'reactivate'
  | 'reject'
  | 'cancel'
  | 'markArrived'
  | 'markSeated'
  | 'markInProgress'
  | 'markCompleted'
  | 'markNoShow'
  | 'markNotStarted'
  | 'reschedule';

export interface AllowedReservationActionsInput {
  status: `${ReservationStatus}`;
  arrivalStatus: `${ArrivalStatus}` | null;
  dateContext: DateContext;
  seatedAndInProgressEnabled: boolean;
  rescheduleEnabled: boolean;
}

export function allowedReservationActions({
  status,
  arrivalStatus,
  dateContext,
  seatedAndInProgressEnabled,
  rescheduleEnabled,
}: AllowedReservationActionsInput): ReadonlySet<ReservationAction> {
  const actions = new Set<ReservationAction>();

  if (status === ReservationStatus.PENDING) {
    actions.add('confirm');
    actions.add('reject');
    return actions;
  }

  if (status === ReservationStatus.CANCELLED) {
    actions.add('reactivate');
    return actions;
  }

  if (status === ReservationStatus.REJECTED) return actions;

  if (dateContext === 'future') {
    actions.add('cancel');
    return actions;
  }

  if (
    arrivalStatus === null ||
    arrivalStatus === ArrivalStatus.WAITING ||
    arrivalStatus === ArrivalStatus.NO_SHOW
  ) {
    actions.add('markArrived');
    actions.add('markNoShow');
    actions.add('cancel');
    return actions;
  }

  if (arrivalStatus === ArrivalStatus.COMPLETED) {
    actions.add('markNotStarted');
    actions.add('markInProgress');
    actions.add('markNoShow');
    if (rescheduleEnabled) actions.add('reschedule');
    actions.add('cancel');
    return actions;
  }

  if (arrivalStatus === ArrivalStatus.ARRIVED) {
    if (seatedAndInProgressEnabled) actions.add('markSeated');
    actions.add('markCompleted');
    actions.add('cancel');
    return actions;
  }

  if (arrivalStatus === ArrivalStatus.SEATED) {
    if (seatedAndInProgressEnabled) actions.add('markInProgress');
    actions.add('markCompleted');
    actions.add('cancel');
    return actions;
  }

  if (arrivalStatus === ArrivalStatus.IN_PROGRESS) {
    actions.add('markCompleted');
    actions.add('cancel');
  }

  return actions;
}
